import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Package, Gift, Loader2, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import WelcomeModal from '../components/WelcomeModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { formatPrice } from '../lib/format';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  new: { hu: 'Új', en: 'New', color: 'bg-blue-600' },
  processing: { hu: 'Feldolgozás alatt', en: 'Processing', color: 'bg-indigo-600' },
  shipped: { hu: 'Elküldve', en: 'Shipped', color: 'bg-amber-600' },
  completed: { hu: 'Teljesítve', en: 'Completed', color: 'bg-green-600' },
  cancelled: { hu: 'Törölve', en: 'Cancelled', color: 'bg-red-600' },
};

function formatApiErrorDetail(detail, language) {
  const map = {
    EMAIL_EXISTS: language === 'hu' ? 'Ez az e-mail cím már regisztrálva van.' : 'This email is already registered.',
    INVALID_CREDENTIALS: language === 'hu' ? 'Hibás e-mail vagy jelszó.' : 'Invalid email or password.',
    PASSWORD_TOO_SHORT: language === 'hu' ? 'A jelszónak legalább 6 karakternek kell lennie.' : 'Password must be at least 6 characters.',
  };
  if (detail == null) return language === 'hu' ? 'Hiba történt. Próbáld újra.' : 'Something went wrong.';
  if (typeof detail === 'string') return map[detail] || detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || '').filter(Boolean).join(' ');
  if (detail && detail.msg) return detail.msg;
  return String(detail);
}

const AuthForms = ({ onRegistered }) => {
  const { t, language } = useLanguage();
  const { login, register } = useCustomerAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const switchMode = (m) => { setMode(m); setError(''); setInfo(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        await axios.post(`${API}/auth/customer/forgot-password`, { email: form.email, reset_base: window.location.origin });
        setInfo(language === 'hu'
          ? 'Ha létezik fiók ezzel az e-mail címmel, elküldtük a visszaállító linket. Nézd meg a postaládád!'
          : 'If an account exists with this email, we have sent a reset link. Check your inbox!');
      } else if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
        if (onRegistered) onRegistered(form.name);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-zinc-900 rounded-xl p-6 md:p-8 border border-zinc-800">
      {mode !== 'forgot' && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => switchMode('login')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'login' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
            data-testid="account-tab-login"
          >
            {language === 'hu' ? 'Belépés' : 'Login'}
          </button>
          <button
            onClick={() => switchMode('register')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'register' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
            data-testid="account-tab-register"
          >
            {language === 'hu' ? 'Regisztráció' : 'Register'}
          </button>
        </div>
      )}

      {mode === 'forgot' && (
        <h2 className="text-lg font-semibold text-white mb-1">{language === 'hu' ? 'Elfelejtett jelszó' : 'Forgot password'}</h2>
      )}
      {mode === 'forgot' && (
        <p className="text-zinc-400 text-sm mb-5">{language === 'hu' ? 'Add meg az e-mail címed, és küldünk egy visszaállító linket.' : 'Enter your email and we will send you a reset link.'}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'register' && (
          <div>
            <Label htmlFor="name" className="text-zinc-300">{language === 'hu' ? 'Név' : 'Name'}</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="account-name-input"
            />
          </div>
        )}
        <div>
          <Label htmlFor="email" className="text-zinc-300">Email</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            required
            data-testid="account-email-input"
          />
        </div>
        {mode !== 'forgot' && (
          <div>
            <Label htmlFor="password" className="text-zinc-300">{language === 'hu' ? 'Jelszó' : 'Password'}</Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="account-password-input"
            />
          </div>
        )}
        {error && <p className="text-red-500 text-sm" data-testid="account-error">{error}</p>}
        {info && <p className="text-green-400 text-sm" data-testid="account-info">{info}</p>}
        <Button type="submit" className="w-full bg-white text-black hover:bg-zinc-200 font-semibold" disabled={loading} data-testid="account-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            mode === 'login' ? (language === 'hu' ? 'Belépés' : 'Login')
              : mode === 'register' ? (language === 'hu' ? 'Regisztráció' : 'Register')
              : (language === 'hu' ? 'Visszaállító link küldése' : 'Send reset link')
          )}
        </Button>
      </form>

      {mode === 'login' && (
        <button
          onClick={() => switchMode('forgot')}
          className="mt-4 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="account-forgot-link"
        >
          {language === 'hu' ? 'Elfelejtetted a jelszavad?' : 'Forgot your password?'}
        </button>
      )}
      {mode === 'forgot' && (
        <button
          onClick={() => switchMode('login')}
          className="mt-4 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="account-back-to-login"
        >
          {language === 'hu' ? '← Vissza a belépéshez' : '← Back to login'}
        </button>
      )}
    </div>
  );
};

const LoyaltyProgram = ({ nextReward, ordersCount }) => {
  const { language } = useLanguage();
  const tiers = [
    { orders: 2, percent: 5, min: 0 },
    { orders: 4, percent: 10, min: 15000 },
    { orders: 7, percent: 15, min: 20000 },
    { orders: 10, percent: 20, min: 25000 },
  ];
  return (
    <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-5 border border-amber-500/20" data-testid="loyalty-panel">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="h-5 w-5 text-amber-400" />
        <h3 className="text-lg font-semibold text-white">{language === 'hu' ? 'Hűségprogram' : 'Loyalty Program'}</h3>
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        {language === 'hu'
          ? `Eddig ${ordersCount} rendelésed volt. Minél többet rendelsz, annál nagyobb kedvezményt kapsz!`
          : `You have placed ${ordersCount} orders so far. The more you order, the bigger discount you get!`}
      </p>
      {nextReward && nextReward.potential_percent > 0 && (
        <div className="mb-4 p-3 bg-amber-500/15 border border-amber-500/30 rounded-lg" data-testid="loyalty-next-reward">
          <p className="text-sm text-amber-300 font-medium">
            {language === 'hu'
              ? `Következő rendelésednél ${nextReward.potential_percent}% kedvezmény érhető el${nextReward.potential_min_order > 0 ? ` (min. ${formatPrice(nextReward.potential_min_order)} Ft feletti rendelésnél)` : ''}!`
              : `Your next order qualifies for a ${nextReward.potential_percent}% discount${nextReward.potential_min_order > 0 ? ` (on orders above ${formatPrice(nextReward.potential_min_order)} HUF)` : ''}!`}
          </p>
        </div>
      )}
      <div className="space-y-1.5">
        {tiers.map((tier) => (
          <div key={tier.orders} className="flex items-center justify-between text-sm border-b border-zinc-800/60 pb-1.5">
            <span className="text-zinc-400">
              {language === 'hu' ? `${tier.orders}. rendeléstől` : `From order #${tier.orders}`}
            </span>
            <span className="text-white font-medium">
              {tier.percent}% {tier.min > 0 ? <span className="text-zinc-500 text-xs">({language === 'hu' ? 'min.' : 'min'} {formatPrice(tier.min)} Ft)</span> : ''}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-zinc-500 mt-3">
        {language === 'hu'
          ? 'A ciklus 10 rendelésenként újraindul. A feloldott kedvezmény a következő 2 rendelésig marad érvényben. Nem vonható össze promóciós kóddal és nem érvényes akciós termékekre.'
          : 'The cycle restarts every 10 orders. An unlocked discount stays valid for the next 2 orders. Cannot be combined with a promo code and does not apply to sale items.'}
      </p>
    </div>
  );
};

const Dashboard = () => {
  const { language } = useLanguage();
  const { customer, logout, authHeader } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [nextReward, setNextReward] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ordersRes, loyaltyRes] = await Promise.all([
          axios.get(`${API}/customer/orders`, { headers: authHeader() }),
          axios.post(`${API}/customer/loyalty`, { subtotal: 100000000, sale_subtotal: 0 }, { headers: authHeader() }),
        ]);
        setOrders(ordersRes.data);
        setNextReward(loyaltyRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authHeader]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-6">
        <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold" data-testid="account-name">{customer.name}</p>
              <p className="text-zinc-500 text-sm">{customer.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full border-zinc-700 text-white hover:bg-zinc-800" onClick={logout} data-testid="account-logout-button">
            <LogOut className="h-4 w-4 mr-2" />
            {language === 'hu' ? 'Kijelentkezés' : 'Log out'}
          </Button>
        </div>
        <LoyaltyProgram nextReward={nextReward} ordersCount={customer.orders_count || 0} />
      </div>

      <div className="lg:col-span-2">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-white" />
          <h2 className="text-xl font-bold text-white">{language === 'hu' ? 'Rendeléseim' : 'My Orders'}</h2>
        </div>
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-500 mx-auto" /></div>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center bg-zinc-900 rounded-xl border border-zinc-800" data-testid="account-no-orders">
            <Package className="h-10 w-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">{language === 'hu' ? 'Még nincs rendelésed.' : 'You have no orders yet.'}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="account-orders-list">
            {orders.map((order) => (
              <div key={order.id} className="bg-zinc-900 rounded-xl p-4 border border-zinc-800" data-testid={`account-order-${order.id.slice(0, 8)}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-white font-medium text-sm">#{order.id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-zinc-500 text-xs flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3" />
                      {new Date(order.created_at).toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-US')}
                    </p>
                  </div>
                  <Badge className={`${statusLabels[order.status]?.color || 'bg-zinc-600'} text-white text-xs`}>
                    {statusLabels[order.status]?.[language] || order.status}
                  </Badge>
                </div>
                <div className="space-y-1 mb-2">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-xs text-zinc-400">
                      <span className="truncate mr-2">{item.name} x{item.quantity}</span>
                      <span>{formatPrice(item.price * item.quantity)} Ft</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-800 pt-2 flex justify-between items-center">
                  {order.discount > 0 && <span className="text-green-500 text-xs">-{formatPrice(order.discount)} Ft</span>}
                  <span className="text-white font-bold ml-auto">{formatPrice(order.total)} Ft</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountPage = () => {
  const { language } = useLanguage();
  const { customer, loading } = useCustomerAuth();
  const navigate = useNavigate();
  const [welcomeName, setWelcomeName] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      {welcomeName && <WelcomeModal name={welcomeName} onClose={() => setWelcomeName(null)} />}
      <div className="container mx-auto max-w-5xl">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8" data-testid="account-title">
          {customer ? (language === 'hu' ? 'Fiókom' : 'My Account') : (language === 'hu' ? 'Belépés / Regisztráció' : 'Login / Register')}
        </h1>
        {customer ? <Dashboard /> : <AuthForms onRegistered={setWelcomeName} />}
      </div>
    </div>
  );
};

export default AccountPage;
